import type { Vec3 } from "@rietz/shared";

/**
 * Debug HUD: the netcode questions (clock sane? how stale? how wrong was
 * prediction?) plus an optional world block fed by `debug/tools.ts` (frame
 * cost, map, nearest exfil, debug layers). Text is rebuilt 5×/s.
 */

const REFRESH_INTERVAL_MS = 200;

export interface OverlayStats {
  status: string;
  /** Round-trip time, ms. */
  pingMs: number;
  /** `pong`-derived server clock offset, ms. */
  offsetMs: number;
  /** Estimated tick the server is on. */
  serverTick: number;
  /** Remote entities currently being rendered. */
  players: number;
  /** Last reconciliation disagreement, metres. */
  reconcileErrM: number;
  /** How the last correction was applied. */
  correction: string;
  /** Age of the newest snapshot, ms; `null` when none has arrived. */
  snapshotAgeMs: number | null;
  /** Commands sent but not yet acknowledged. */
  pendingCmds: number;
  pos: Vec3;
  stance: string;
  /** Horizontal speed, m/s. */
  speed: number;
  stamina: number;
}

/**
 * The renderer/world block, supplied by `DebugTools`; omitted when the tools
 * are not wired up.
 */
export interface OverlayExtras {
  mapId: string;
  solidCount: number;
  propCount: number;
  /** Breakable window panes on the current map. */
  paneCount: number;
  /**
   * Live from `renderer.info.render`, NOT the load-time estimate, which cannot
   * see avatars or the debug layers.
   */
  drawCalls: number;
  triangles: number;
  /** Empty when the map has no extracts. */
  nearestExtractName: string;
  /** Metres to the nearest point of that extract's volume. */
  nearestExtractDistM: number;
  /** Compact one-line summary of which layers are on. */
  toggles: string;
  /** True while F6 noclip is flying the player. */
  noclip: boolean;
}

export class DebugOverlay {
  private readonly el: HTMLElement;

  private frames = 0;
  private fps = 0;
  private windowStartMs = 0;
  private nextRefreshMs = 0;

  /** Newest timestamp seen by `beginFrame` — drives the help block's expiry. */
  private nowMs = 0;
  private helpText = "";
  private helpUntilMs = 0;

  constructor(el: HTMLElement) {
    this.el = el;
  }

  /**
   * Once per rendered frame: accumulates the FPS counter and returns whether to
   * gather stats and `render`.
   */
  beginFrame(nowMs: number): boolean {
    if (this.windowStartMs === 0) this.windowStartMs = nowMs;
    this.frames += 1;
    this.nowMs = nowMs;

    if (nowMs < this.nextRefreshMs) return false;
    this.nextRefreshMs = nowMs + REFRESH_INTERVAL_MS;

    const elapsed = nowMs - this.windowStartMs;
    if (elapsed > 0) this.fps = (this.frames * 1000) / elapsed;
    this.frames = 0;
    this.windowStartMs = nowMs;
    return true;
  }

  /**
   * Shows `text` under the stats for `durationMs` — the keybind help at boot;
   * it disappears on its own.
   */
  showHelp(text: string, durationMs: number): void {
    this.helpText = text;
    // `beginFrame` has not necessarily run yet at boot.
    const now = this.nowMs === 0 ? performance.now() : this.nowMs;
    this.helpUntilMs = now + durationMs;
  }

  render(s: OverlayStats, extras?: OverlayExtras): void {
    const age =
      s.snapshotAgeMs === null ? "  --" : s.snapshotAgeMs.toFixed(0).padStart(4);

    let text = [
      `net    ${s.status}`,
      `ping   ${s.pingMs.toFixed(1).padStart(6)} ms   offset ${fmt(s.offsetMs, 0)} ms`,
      `tick   ${s.serverTick.toFixed(0).padStart(6)}      snap   ${age} ms`,
      `recon  ${(s.reconcileErrM * 1000).toFixed(1).padStart(6)} mm   ${s.correction}  (${s.pendingCmds} pend)`,
      `pos    ${fmt(s.pos.x, 2)} ${fmt(s.pos.y, 2)} ${fmt(s.pos.z, 2)}`,
      `move   ${s.speed.toFixed(2).padStart(6)} m/s  ${s.stance}  stam ${s.stamina.toFixed(0)}`,
      `others ${s.players.toFixed(0).padStart(6)}      fps    ${this.fps.toFixed(0).padStart(4)}`,
    ].join("\n");

    if (extras !== undefined) {
      const exfil =
        extras.nearestExtractName === ""
          ? "none"
          : `${extras.nearestExtractDistM.toFixed(1)} m  ${extras.nearestExtractName}`;
      text += `\n${[
        `draw   ${extras.drawCalls.toFixed(0).padStart(6)} calls  ${kilo(extras.triangles)} tris`,
        `map    ${extras.mapId}  solids ${extras.solidCount}  props ${extras.propCount}  ` +
          `glass ${extras.paneCount}`,
        `exfil  ${exfil}`,
        `debug  ${extras.toggles}`,
      ].join("\n")}`;

      if (extras.noclip) text += "\nNOCLIP — collision and gravity off";
    }

    if (this.helpText !== "" && this.nowMs < this.helpUntilMs) {
      text += `\n\n${this.helpText}`;
    } else if (this.helpText !== "") {
      this.helpText = "";
    }

    this.el.textContent = text;
  }

  /** Replaces the HUD with a single line — used for fatal errors. */
  message(text: string): void {
    this.el.textContent = text;
  }
}

function fmt(v: number, digits: number): string {
  return v.toFixed(digits).padStart(7);
}

function kilo(v: number): string {
  return v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v.toFixed(0);
}
